import { useEffect, useMemo, useRef, useState } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { PublicFooterLegalLinks } from "./public-legal.js";

type Accent = "blue" | "yellow" | "green" | "red";
type CoverVariant = "orbit" | "signal" | "archive";
type WorkCategory = "research" | "skills";

type Cover = {
  index: string;
  label: string;
  title: string;
  body: string;
  accent: Accent;
  variant: CoverVariant;
  imageSrc?: string;
  href?: string;
};

const headerLinks: Array<{ id: string; label: string; href?: string; workCategory?: WorkCategory }> = [
  { id: "lab-line-news", label: "个人文章", href: "#/articles" },
  { id: "lab-line-work", label: "市场研究", workCategory: "research" },
  { id: "lab-line-work", label: "SKILL推荐", workCategory: "skills" },
  { id: "lab-line-contact", label: "联系" }
] as const;

const tickerItems: Array<{ section: string; meta: string; title: string; accent: Accent; href: string }> = [
  { section: "个人文章", meta: "欢迎手记", title: "欢迎来到云宸LAB", accent: "blue", href: "#/articles/welcome-to-yunchen-lab" },
  { section: "研究方法", meta: "证据整理", title: "线索、事实和解释为什么要分开", accent: "yellow", href: "#/articles/clues-facts-and-interpretations" },
  { section: "市场研究", meta: "专题入口", title: "公开研究内容正在整理", accent: "green", href: "#lab-line-work" },
  { section: "Skill推荐", meta: "工具观察", title: "记录值得长期使用的工作方法", accent: "red", href: "#lab-line-work" }
];

const covers: Cover[] = [
  {
    index: "01",
    label: "STRUCTURE / 结构观察",
    title: "先看关系，再看结论。",
    body: "把一段市场叙事拆成可以核对的关系：谁在变化，什么没有变化，哪些线索仍然需要时间回答。",
    accent: "blue",
    variant: "orbit",
    imageSrc: "/assets/article-welcome-cover-v1.png",
    href: "#/articles/welcome-to-yunchen-lab"
  },
  {
    index: "02",
    label: "EVIDENCE / 证据整理",
    title: "让每条线索都带着来处。",
    body: "公开资料、数据口径与研究判断各自站好位置。先记录证据，再说明它能够支持到哪一步。",
    accent: "yellow",
    variant: "signal",
    imageSrc: "/assets/article-evidence-cover-v1.png",
    href: "#/articles/clues-facts-and-interpretations"
  },
  {
    index: "03",
    label: "REVIEW / 研究复盘",
    title: "把一次判断放回时间里。",
    body: "保留当时看见的事实、采用的假设和后来出现的反例，让复盘成为下一次研究可以使用的方法。",
    accent: "green",
    variant: "archive"
  }
];

const workFilters: Array<{ id: WorkCategory; label: string }> = [
  { id: "research", label: "市场研究" },
  { id: "skills", label: "Skill 推荐" }
];

const workEmptyCopy: Record<WorkCategory, { eyebrow: string; title: string; body: string }> = {
  research: { eyebrow: "MARKET RESEARCH", title: "市场研究正在整理。", body: "第一篇公开研究发布后，会从这里进入。" },
  skills: { eyebrow: "SKILL NOTES", title: "Skill 推荐正在整理。", body: "只展示实际使用和验证过的工具，不用占位内容填满页面。" }
};

const newsItems: Array<{ label: string; date: string; title: string; variant: CoverVariant; href: string }> = [
  { label: "LAB NOTE / 01", date: "2026.09.04", title: "欢迎来到云宸LAB：这里如何记录市场、证据与复盘", variant: "orbit", href: "#/articles/welcome-to-yunchen-lab" },
  { label: "LAB NOTE / 02", date: "2026.09.04", title: "线索、事实和解释，为什么要分开摆放", variant: "signal", href: "#/articles/clues-facts-and-interpretations" }
];

const searchItems = [
  { label: "专题", title: "研究专题", href: "#lab-line-work" },
  { label: "Skill", title: "Skill 推荐", href: "#lab-line-work" },
  { label: "动态", title: "研究动态", href: "#lab-line-news" },
  { label: "作者", title: "关于云宸", href: "#lab-line-profile" },
  { label: "文章", title: "欢迎来到云宸LAB", href: "#/articles/welcome-to-yunchen-lab" },
  { label: "文章", title: "线索、事实和解释", href: "#/articles/clues-facts-and-interpretations" },
  { label: "归档", title: "全部公开文章", href: "#/articles" }
];

function CoverArt({ variant }: { variant: CoverVariant }): React.JSX.Element {
  if (variant === "signal") {
    return <svg className="lab-line-cover-art" viewBox="0 0 420 380" aria-hidden="true">
      <g className="lab-line-cover-art-line" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <rect x="54" y="62" width="286" height="226" rx="19" strokeWidth="6" />
        <path d="M88 117h168M88 151h108M88 185h133" strokeWidth="6" />
        <path d="M86 246c43-46 64 31 111-6s69-64 114-11" strokeWidth="8" />
        <circle cx="300" cy="102" r="31" strokeWidth="5" />
        <path d="m324 126 28 28" strokeWidth="10" />
      </g>
      <path className="lab-line-cover-art-accent" d="M104 76c17-30 45-37 69-14" fill="none" stroke="#155eef" strokeLinecap="round" strokeWidth="13" />
      <circle className="lab-line-cover-art-dot" cx="88" cy="79" r="11" fill="#f27f6c" />
      <circle className="lab-line-cover-art-dot" cx="297" cy="249" r="14" fill="#58c79b" />
    </svg>;
  }

  if (variant === "archive") {
    return <svg className="lab-line-cover-art" viewBox="0 0 420 380" aria-hidden="true">
      <g className="lab-line-cover-art-line" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M82 88 207 49l127 39-127 41Z" strokeWidth="6" />
        <path d="M82 88v168l125 42 127-42V88M207 130v168" strokeWidth="6" />
        <path d="M120 171 207 198l89-28M120 210l87 28 89-28" strokeWidth="6" />
        <path d="M52 310h294" strokeWidth="5" />
      </g>
      <path className="lab-line-cover-art-accent" d="M95 84 207 116l112-34" fill="none" stroke="#155eef" strokeLinecap="round" strokeWidth="11" />
      <circle className="lab-line-cover-art-dot" cx="82" cy="88" r="12" fill="#f4c85f" />
      <circle className="lab-line-cover-art-dot" cx="333" cy="88" r="12" fill="#f27f6c" />
      <circle className="lab-line-cover-art-dot" cx="207" cy="299" r="15" fill="#58c79b" />
    </svg>;
  }

  return <svg className="lab-line-cover-art" viewBox="0 0 420 380" aria-hidden="true">
    <g className="lab-line-cover-art-line" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="206" cy="188" r="108" strokeWidth="6" />
      <ellipse cx="206" cy="188" rx="157" ry="57" strokeWidth="5" transform="rotate(-18 206 188)" />
      <ellipse cx="206" cy="188" rx="157" ry="57" strokeWidth="5" transform="rotate(44 206 188)" />
      <path d="M114 238c28-27 53-23 79 2s48 29 78 2" strokeWidth="7" />
      <path d="M153 159c16-18 33-18 51 0M226 159c16-18 33-18 51 0" strokeWidth="7" />
    </g>
    <path className="lab-line-cover-art-accent" d="M206 58v43M96 188h42M274 188h42" fill="none" stroke="#155eef" strokeLinecap="round" strokeWidth="11" />
    <circle className="lab-line-cover-art-dot" cx="99" cy="92" r="12" fill="#f4c85f" />
    <circle className="lab-line-cover-art-dot" cx="311" cy="278" r="14" fill="#f27f6c" />
    <circle className="lab-line-cover-art-dot" cx="201" cy="77" r="14" fill="#58c79b" />
  </svg>;
}

function WorkArt({ variant }: { variant: CoverVariant }): React.JSX.Element {
  return <svg className="lab-line-work-art" viewBox="0 0 220 150" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
      {variant === "orbit" && <><circle cx="108" cy="76" r="44" /><ellipse cx="108" cy="76" rx="78" ry="26" transform="rotate(-18 108 76)" /><path d="M72 76h72" /></>}
      {variant === "signal" && <><rect x="42" y="29" width="135" height="94" rx="12" /><path d="M62 57h68M62 76h45M62 98c24-30 39 18 67-7s37-10 49-1" /></>}
      {variant === "archive" && <><path d="m108 23 60 24v72l-60 24-60-24V47Z" /><path d="M48 47l60 24 60-24M108 71v72" /><path d="M76 51c11-13 22-14 32-3" /></>}
    </g>
    <circle cx="31" cy="29" r="7" fill="currentColor" />
    <circle cx="188" cy="120" r="8" fill="currentColor" />
  </svg>;
}

function ProfileSceneArt(): React.JSX.Element {
  return <div className="lab-line-profile-scene-art" aria-hidden="true">
    <img src="/assets/profile-researcher-v1.webp" alt="" width="1536" height="1024" loading="lazy" decoding="async" />
    <span className="lab-line-profile-scene-index">FIELD NOTE / 001</span>
    <span className="lab-line-profile-scene-note">FACT · SOURCE · REVIEW</span>
  </div>;
}

function LivingFooterArt(): React.JSX.Element {
  return <div className="lab-line-footer-scene" aria-hidden="true">
    <span>RESEARCH, REVIEW, REPEAT.</span>
    <strong>一隅，记录，生长。</strong>
    <small>YUNCHEN LAB · 2026</small>
  </div>;
}

function DirectionalArrow({ direction = "right" }: { direction?: "left" | "right" }): React.JSX.Element {
  return <svg className={`lab-line-arrow-icon lab-line-arrow-icon--${direction}`} viewBox="0 0 42 18" aria-hidden="true">
    <path d="M2 9h36M31 2l7 7-7 7" />
  </svg>;
}

function PrimaryNav({ onNavigate, onSelectWorkCategory }: { onNavigate?: () => void; onSelectWorkCategory?: (category: WorkCategory) => void }): React.JSX.Element {
  return <>{headerLinks.map(({ id, label, href, workCategory }) => <a key={`${id}-${label}`} href={href ?? `#${id}`} onClick={() => {
    if (workCategory) onSelectWorkCategory?.(workCategory);
    onNavigate?.();
  }}><span>{label}</span></a>)}</>;
}

function HeaderStatus(): React.JSX.Element {
  return <div className="lab-line-header-status">
    <button className="lab-line-header-account" type="button" aria-label="登录功能建设中" title="登录功能建设中" disabled>
      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="9" r="3" /><path d="M6.8 18.2c.9-2.5 2.7-3.8 5.2-3.8s4.3 1.3 5.2 3.8" /></svg>
    </button>
  </div>;
}

function NewsTicker(): React.JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    const sequence = track?.querySelector<HTMLElement>(".lab-line-ticker-sequence");
    if (!track || !sequence) return;
    const updateDuration = (): void => {
      const seconds = Math.min(36, Math.max(24, sequence.getBoundingClientRect().width / 48));
      track.style.setProperty("--lab-ticker-duration", `${seconds.toFixed(2)}s`);
    };
    updateDuration();
    if (!("ResizeObserver" in window)) return;
    const observer = new ResizeObserver(updateDuration);
    observer.observe(sequence);
    return () => observer.disconnect();
  }, []);

  return <div className="lab-line-ticker" aria-label="云宸LAB研究动态">
    <div className="lab-line-ticker-track" ref={trackRef}>
      {[0, 1].map((copy) => <div className="lab-line-ticker-sequence" key={copy} aria-hidden={copy === 1}>
        {tickerItems.map((item) => <a href={item.href} key={`${copy}-${item.section}-${item.meta}`}>
          <i className={`lab-line-dot lab-line-dot--${item.accent}`} aria-hidden="true" />
          <b>{item.section}</b>
          <span>{item.meta}</span>
          <em>{item.title}</em>
        </a>)}
      </div>)}
    </div>
  </div>;
}

function CoverCarousel(): React.JSX.Element {
  const [activeIndex, setActiveIndex] = useState(1);
  const [dragOffset, setDragOffset] = useState(0);
  const [transitionLocked, setTransitionLocked] = useState(false);
  const [wrapMotion, setWrapMotion] = useState<{ index: number; direction: "next" | "previous" } | null>(null);
  const activeIndexRef = useRef(activeIndex);
  const transitionLockRef = useRef(false);
  const wrapTimerRef = useRef<number | null>(null);
  const pointerState = useRef<{ id: number; startX: number; latestX: number } | null>(null);
  const suppressClick = useRef(false);
  const active = covers[activeIndex] ?? covers[0]!;
  const move = (step: number): void => {
    setDragOffset(0);
    if (transitionLockRef.current) return;
    transitionLockRef.current = true;
    setTransitionLocked(true);
    const current = activeIndexRef.current;
    const next = (current + step + covers.length) % covers.length;
    const direction = step > 0 ? "next" : "previous";
    const wrappingIndex = step > 0 ? (current - 1 + covers.length) % covers.length : (current + 1) % covers.length;
    activeIndexRef.current = next;
    setWrapMotion({ index: wrappingIndex, direction });
    setActiveIndex(next);
    if (wrapTimerRef.current !== null) window.clearTimeout(wrapTimerRef.current);
    wrapTimerRef.current = window.setTimeout(() => {
      setWrapMotion(null);
      transitionLockRef.current = false;
      setTransitionLocked(false);
    }, 650);
  };

  useEffect(() => () => {
    if (wrapTimerRef.current !== null) window.clearTimeout(wrapTimerRef.current);
  }, []);

  const moveTo = (index: number): void => {
    if (index === activeIndex) return;
    const forwardDistance = (index - activeIndex + covers.length) % covers.length;
    move(forwardDistance === 1 ? 1 : -1);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.pointerType === "mouse" || transitionLockRef.current) return;
    suppressClick.current = false;
    pointerState.current = { id: event.pointerId, startX: event.clientX, latestX: event.clientX };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const pointer = pointerState.current;
    if (!pointer || pointer.id !== event.pointerId) return;
    pointer.latestX = event.clientX;
    const delta = Math.max(-120, Math.min(120, event.clientX - pointer.startX));
    if (Math.abs(delta) >= 48) suppressClick.current = true;
    setDragOffset(delta);
  };

  const finishPointer = (event: React.PointerEvent<HTMLDivElement>, cancelled = false): void => {
    const pointer = pointerState.current;
    if (!pointer || pointer.id !== event.pointerId) return;
    const delta = pointer.latestX - pointer.startX;
    pointerState.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (!cancelled && Math.abs(delta) >= 48) move(delta < 0 ? 1 : -1);
    else setDragOffset(0);
  };

  return <section className="lab-line-cover-carousel" aria-labelledby="lab-line-cover-title" onKeyDown={(event) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      move(event.key === "ArrowLeft" ? -1 : 1);
    }
  }}>
    <div className="lab-line-cover-carousel-head"><span>SELECTED WORK / 重点研究</span><small>MANUAL SELECT</small></div>
    <div className={`lab-line-cover-viewport${dragOffset ? " is-dragging" : ""}`} style={{ "--lab-hero-drag": `${dragOffset}px` } as React.CSSProperties} role="region" aria-roledescription="轮播" aria-label="重点研究观察" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={(event) => finishPointer(event)} onPointerCancel={(event) => finishPointer(event, true)} onLostPointerCapture={(event) => finishPointer(event, true)}>
      {covers.map((cover, coverIndex) => {
        const relative = (coverIndex - activeIndex + covers.length) % covers.length;
        const position = relative === 0 ? "center" : relative === 1 ? "right" : "left";
        const wrapClass = wrapMotion?.index === coverIndex ? ` lab-line-cover--wrap-${wrapMotion.direction}` : "";
        return <article className={`lab-line-cover lab-line-cover--${position} lab-line-cover--${cover.accent}${wrapClass}`} key={cover.index}>
        <a className="lab-line-cover-frame" href={cover.href ?? "#lab-line-observations"} aria-label={coverIndex === activeIndex && cover.href ? `打开文章：${cover.title}` : `查看${cover.title}`} aria-current={cover.index === active.index ? "true" : undefined} onClick={(event) => {
          if (suppressClick.current) {
            event.preventDefault();
            suppressClick.current = false;
            return;
          }
          if (coverIndex !== activeIndex) {
            event.preventDefault();
            moveTo(coverIndex);
          }
        }}>
          <span className="lab-line-cover-media">
            <span className="lab-line-cover-number">{cover.index}</span>
            {cover.imageSrc ? <img className="lab-line-cover-art lab-line-cover-image" src={cover.imageSrc} alt="" /> : <CoverArt variant={cover.variant} />}
            <span className="lab-line-cover-hover" aria-hidden="true"><small>{cover.label}</small><strong>{cover.title}</strong><i /></span>
            <span className="lab-line-cover-corner" aria-hidden="true" />
          </span>
        </a>
        <div className="lab-line-cover-caption"><span>{cover.label}</span><h3>{cover.title}</h3></div>
      </article>;
      })}
    </div>
    <div className="lab-line-cover-carousel-foot">
      <div className="lab-line-cover-progress" aria-hidden="true"><i style={{ width: `${((activeIndex + 1) / covers.length) * 100}%` }} /></div>
      <div className="lab-line-cover-controls">
        <button type="button" aria-label="上一张研究封面" aria-disabled={transitionLocked} onClick={() => move(-1)}><DirectionalArrow direction="left" /><small>上一条</small><i className="lab-line-control-line" aria-hidden="true" /></button>
        <span id="lab-line-cover-title">0{activeIndex + 1} / 0{covers.length}</span>
        <button type="button" aria-label="下一张研究封面" aria-disabled={transitionLocked} onClick={() => move(1)}><small>下一条</small><DirectionalArrow /><i className="lab-line-control-line" aria-hidden="true" /></button>
      </div>
    </div>
    <p className="lab-line-cover-description" aria-live="polite">{active.body}</p>
  </section>;
}

function SearchPanel({ query, setQuery, onClose }: { query: string; setQuery: (value: string) => void; onClose: () => void }): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const matches = useMemo(() => {
    const value = query.trim().toLowerCase();
    return value ? searchItems.filter((item) => `${item.label} ${item.title}`.toLowerCase().includes(value)) : searchItems;
  }, [query]);

  const trapFocus = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Tab") return;
    const focusable = Array.from(panelRef.current?.querySelectorAll<HTMLElement>("input, a[href], button:not([disabled])") ?? []);
    if (!focusable.length) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return <div className="lab-line-search-layer">
    <button className="lab-line-search-scrim" type="button" aria-label="关闭搜索" onClick={onClose} />
    <div ref={panelRef} id="lab-line-search-panel" className="lab-line-search-panel" role="dialog" aria-modal="true" aria-labelledby="lab-line-search-title" aria-describedby="lab-line-search-description" onKeyDown={trapFocus}>
      <div className="lab-line-search-head"><span>云宸LAB · 研究索引</span><h2 id="lab-line-search-title">搜索研究</h2><p id="lab-line-search-description">从公开专题、Skill 推荐与个人文章中，找到下一条值得继续追踪的线索。</p></div>
      <label className="lab-line-search-field">
        <svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="20" cy="20" r="10" /><path d="m27.5 27.5 10 10" /></svg>
        <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="输入关键词，例如：结构、证据、复盘" aria-label="搜索研究入口" />
      </label>
      <div className="lab-line-search-results" aria-live="polite">{matches.length ? matches.map((item) => <a key={item.label} href={item.href} onClick={onClose}><span>{item.label}</span><b>{item.title}</b><em aria-hidden="true"><DirectionalArrow /></em></a>) : <p>暂时没有匹配的研究入口。</p>}</div>
      <button className="lab-line-search-close" type="button" aria-label="关闭搜索" onClick={onClose}><svg viewBox="0 0 48 48" aria-hidden="true"><path d="M12 12l24 24M36 12 12 36" /></svg></button>
    </div>
  </div>;
}

function FloatingSearchButton({ expanded, onOpen, buttonRef }: { expanded: boolean; onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void; buttonRef: React.RefObject<HTMLButtonElement | null> }): React.JSX.Element {
  return <button ref={buttonRef} className="lab-line-global-search-trigger" type="button" aria-label="打开研究搜索" aria-expanded={expanded} aria-controls="lab-line-search-panel" onClick={onOpen}>
    <span className="lab-line-search-trigger-core" aria-hidden="true">
      <span className="lab-line-search-trigger-motion"><svg className="lab-line-search-trigger-icon" viewBox="0 0 48 48"><circle cx="20" cy="20" r="10" /><path d="m27.5 27.5 10 10" /></svg></span>
    </span>
    <span className="lab-line-search-trigger-label" aria-hidden="true">搜索</span>
  </button>;
}

export function PublicHomePage(): React.JSX.Element {
  const pageRef = useRef<HTMLDivElement>(null);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const searchReturnFocusRef = useRef<HTMLButtonElement | null>(null);
  const lenisRef = useRef<Lenis | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [workCategory, setWorkCategory] = useState<WorkCategory>("research");

  useEffect(() => {
    const query = window.location.hash.split("?")[1];
    if (!query) return;
    const parameters = new URLSearchParams(query);
    const tab = parameters.get("tab");
    if (tab === "research" || tab === "skills") setWorkCategory(tab);
    const sectionId = parameters.get("section");
    if (!sectionId) return;
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => document.getElementById(sectionId)?.scrollIntoView({ block: "start" })));
  }, []);

  const closeSearch = (): void => {
    setSearchOpen(false);
    setSearchQuery("");
    window.requestAnimationFrame(() => searchReturnFocusRef.current?.focus());
  };

  const openSearch = (event: React.MouseEvent<HTMLButtonElement>): void => {
    searchReturnFocusRef.current = event.currentTarget;
    setMenuOpen(false);
    setSearchOpen(true);
  };

  const closePanels = (): void => {
    setMenuOpen(false);
    if (searchOpen) closeSearch();
  };

  useEffect(() => {
    if (!menuOpen && !searchOpen) return;
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (searchOpen) closeSearch();
      else setMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen, searchOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => { document.documentElement.style.overflow = previousOverflow; };
  }, [searchOpen]);

  useEffect(() => {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!finePointer.matches || window.innerWidth <= 768) return;

    const lenis = new Lenis({
      autoRaf: true,
      smoothWheel: true,
      syncTouch: false,
      lerp: .1,
      wheelMultiplier: .95,
      stopInertiaOnNavigate: true,
      respectReducedMotion: true,
      prevent: (node) => Boolean(node.closest(".lab-line-search-panel, .lab-line-mobile-menu"))
    });
    lenisRef.current = lenis;

    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (searchOpen) lenisRef.current?.stop();
    else lenisRef.current?.start();
  }, [searchOpen]);

  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const targets = Array.from(root.querySelectorAll<HTMLElement>("[data-lab-reveal]"));
    root.classList.add("lab-line-reveal-ready");
    if (!("IntersectionObserver" in window)) {
      targets.forEach((target) => target.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        (entry.target as HTMLElement).classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -10%", threshold: .12 });
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [workCategory]);

  return <div className="lab-line-page" ref={pageRef}>
    <header className="lab-line-header">
      <a className="lab-line-brand" href="#/home" aria-label="返回云宸LAB首页" onClick={closePanels}>
        <span aria-hidden="true">云</span>
        <b>云宸LAB<small>YUNCHEN LAB</small></b>
      </a>
      <div className="lab-line-header-intro"><span>个人学习记录网站</span><small>记录学习、研究与成长。</small></div>
      <nav className="lab-line-desktop-nav" aria-label="网站导航">
        <PrimaryNav onSelectWorkCategory={setWorkCategory} />
        <HeaderStatus />
      </nav>
    </header>

    <NewsTicker />

    <div className="lab-line-mobile-panels">
      {menuOpen && <button className="lab-line-menu-scrim" type="button" aria-label="关闭菜单" onClick={() => setMenuOpen(false)} />}
      <div id="lab-line-mobile-menu" className="lab-line-mobile-menu" role="dialog" aria-modal="true" aria-label="移动端网站导航" hidden={!menuOpen}>
        <div className="lab-line-mobile-menu-head"><span>MENU / 云宸LAB</span><button type="button" aria-label="关闭菜单" onClick={() => setMenuOpen(false)}>×</button></div>
        <nav aria-label="移动端网站导航"><PrimaryNav onNavigate={() => setMenuOpen(false)} onSelectWorkCategory={setWorkCategory} /></nav>
      </div>
    </div>

    {searchOpen && <SearchPanel query={searchQuery} setQuery={setSearchQuery} onClose={closeSearch} />}
    <main id="main-content">
      <section className="lab-line-cover-hero" id="lab-line-observations" aria-labelledby="lab-line-cover-hero-title">
        <div className="lab-line-cover-hero-intro"><span className="lab-line-label">COVER / PUBLIC RESEARCH</span><h1 id="lab-line-cover-hero-title">一页一页，<br /><em>把问题看清。</em></h1><p>云宸LAB是一份独立的 A 股研究档案。这里收集结构、证据与复盘，不急着替你给出答案。</p></div>
        <CoverCarousel />
        <div className="lab-line-cover-hero-note" aria-hidden="true"><span>OPEN / 2026</span><i /></div>
      </section>

      <section className="lab-line-section lab-line-work" id="lab-line-work" aria-labelledby="lab-line-work-title">
        <div className="lab-line-display-heading" data-lab-reveal="heading"><span>WORK</span><h2 id="lab-line-work-title">研究专题</h2><i aria-hidden="true" /></div>
        <div className="lab-line-work-filters" data-lab-reveal="line" role="tablist" aria-orientation="horizontal" aria-label="研究专题分类">
          {workFilters.map((filter) => <button key={filter.id} type="button" role="tab" aria-selected={workCategory === filter.id} aria-controls="lab-line-work-content" onClick={() => setWorkCategory(filter.id)}><span>{filter.label}</span></button>)}
        </div>
        <div className="lab-line-work-empty" id="lab-line-work-content" role="tabpanel" data-lab-reveal="work" key={workCategory}>
          <span>{workEmptyCopy[workCategory].eyebrow}</span>
          <h3>{workEmptyCopy[workCategory].title}</h3>
          <p>{workEmptyCopy[workCategory].body}</p>
        </div>
      </section>

      <section className="lab-line-profile" id="lab-line-profile" aria-labelledby="lab-line-profile-title">
        <div className="lab-line-profile-inner lab-line-profile-scene" data-lab-reveal="profile">
          <ProfileSceneArt />
          <article className="lab-line-profile-copy">
            <span className="lab-line-label">PROFILE / 云宸</span>
            <h2 id="lab-line-profile-title">我想做什么？</h2>
            <p>在AI时代中不断学习成长，跟上时代的发展，感受未来</p>
            <ul className="lab-line-profile-tags" aria-label="云宸LAB研究标签"><li>市场研究</li><li>SKILL推荐</li><li>AI · 数据实验</li></ul>
            <aside className="lab-line-profile-sidekick"><b>小云 · 阿宸</b></aside>
          </article>
        </div>
      </section>

      <section className="lab-line-section lab-line-news" id="lab-line-news" aria-labelledby="lab-line-news-title">
        <div className="lab-line-news-heading" data-lab-reveal="heading"><div><span className="lab-line-label">NEWS / NOTES</span><h2 id="lab-line-news-title">个人文章</h2></div><p>从欢迎手记开始，再读一篇关于线索、事实与解释的方法文章。<a href="#/articles">查看全部文章 →</a></p></div>
        <div className="lab-line-news-list">{newsItems.map((item) => <article key={item.label} data-lab-reveal="news"><a className="lab-line-news-row" href={item.href} aria-label={`阅读文章：${item.title}`}><time className="lab-line-news-date" dateTime={item.date.replace(/\./g, "-")}>{item.date}</time><div className="lab-line-news-main"><div className="lab-line-news-thumb"><WorkArt variant={item.variant} /></div><div className="lab-line-news-copy"><span>{item.label}</span><h3>{item.title}</h3></div></div><span className="lab-line-news-link" aria-hidden="true"><DirectionalArrow /></span></a></article>)}</div>
      </section>

      <section className="lab-line-contact" id="lab-line-contact" aria-labelledby="lab-line-contact-title"><div data-lab-reveal="heading"><span className="lab-line-label">CONTACT / 从问题开始</span><h2 id="lab-line-contact-title">从一封邮件开始。</h2><p>研究交流与网站反馈，请发送至 yunchenlab@yeah.net。</p></div><a className="lab-line-contact-cta" data-lab-reveal="contact" href="mailto:yunchenlab@yeah.net"><span>邮件联系</span><svg viewBox="0 0 32 32" aria-hidden="true"><path d="M5 16h20M18 9l7 7-7 7" /></svg></a></section>
    </main>

    <FloatingSearchButton expanded={searchOpen} onOpen={openSearch} buttonRef={searchTriggerRef} />

    <nav className="lab-line-mobile-bar" aria-label="移动端工具栏"><button type="button" aria-expanded={searchOpen} aria-controls="lab-line-search-panel" onClick={(event) => { if (searchOpen) closeSearch(); else openSearch(event); }}><span aria-hidden="true">⌕</span> 搜索</button><button type="button" aria-expanded={menuOpen} aria-controls="lab-line-mobile-menu" onClick={() => { setMenuOpen((open) => !open); if (searchOpen) closeSearch(); }}>MENU <span aria-hidden="true">+</span></button></nav>

    <footer className="lab-line-footer" data-lab-reveal="footer"><LivingFooterArt /><div className="lab-line-footer-meta"><div className="lab-line-footer-brand"><b>云宸LAB</b><span>独立市场研究 · 数据、证据与复盘</span></div><nav aria-label="页脚导航"><PrimaryNav onSelectWorkCategory={setWorkCategory} /></nav><p><PublicFooterLegalLinks /></p></div></footer>
  </div>;
}
